import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as InstallIcon,
  PhoneIphone as PhoneIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

const PWAInstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSamsungBrowser, setIsSamsungBrowser] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode = () =>
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');

    setIsStandalone(isInStandaloneMode());

    // Detect device and browser
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if Android
    const android = /android/i.test(userAgent);
    setIsAndroid(android);

    // Check if Samsung Browser
    const samsung = /SamsungBrowser/i.test(userAgent);
    setIsSamsungBrowser(samsung);

    // Check if user already dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    // Show prompt if not dismissed or if 1 day has passed
    const shouldShow = !dismissed || (now - dismissedTime) > oneDay;

    // Listen for the beforeinstallprompt event (Chrome, Edge, Samsung Browser, etc.)
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      e.preventDefault();
      setDeferredPrompt(e);

      if (shouldShow && !isInStandaloneMode()) {
        // Wait a bit before showing (better UX)
        setTimeout(() => {
          setShowPrompt(true);
          setShowBanner(true);
        }, 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For browsers that don't support beforeinstallprompt or already have it installed
    // Show manual instructions after a delay
    if (!isInStandaloneMode() && shouldShow) {
      setTimeout(() => {
        // If no deferredPrompt was set after 3 seconds, show manual instructions
        if (!deferredPrompt && (iOS || android)) {
          setShowPrompt(true);
          setShowBanner(true);
        }
      }, 3000);
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setShowPrompt(false);
      setShowBanner(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For browsers without native install prompt, keep dialog open to show instructions
      return;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setShowPrompt(false);
        setShowBanner(false);
      } else {
        console.log('User dismissed the install prompt');
        // Keep showing manual instructions
      }

      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleBannerClose = () => {
    setShowBanner(false);
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  // Render instructions based on browser/device
  const renderInstructions = () => {
    if (isIOS) {
      return (
        <Box
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            p: 2,
            mt: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('pwaInstall.iosInstructions.title')}
          </Typography>
          <Typography variant="body2" component="div">
            1. {t('pwaInstall.iosInstructions.step1')} <Box component="span" sx={{ fontSize: '1.2em' }}>⎙</Box>
            <br />
            2. {t('pwaInstall.iosInstructions.step2')}
            <br />
            3. {t('pwaInstall.iosInstructions.step3')}
          </Typography>
        </Box>
      );
    }

    if (isSamsungBrowser) {
      return (
        <Box
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            p: 2,
            mt: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('pwaInstall.samsungInstructions.title')}
          </Typography>
          <Typography variant="body2" component="div">
            1. {t('pwaInstall.samsungInstructions.step1')} <Box component="span" sx={{ fontSize: '1.2em' }}>☰</Box>
            <br />
            2. {t('pwaInstall.samsungInstructions.step2')} <HomeIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
            <br />
            3. {t('pwaInstall.samsungInstructions.step3')}
          </Typography>
        </Box>
      );
    }

    if (isAndroid && !deferredPrompt) {
      return (
        <Box
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            p: 2,
            mt: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('pwaInstall.androidInstructions.title')}
          </Typography>
          <Typography variant="body2" component="div">
            1. {t('pwaInstall.androidInstructions.step1')} <Box component="span" sx={{ fontSize: '1.2em' }}>⋮</Box>
            <br />
            2. {t('pwaInstall.androidInstructions.step2')}
            <br />
            3. {t('pwaInstall.androidInstructions.step3')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          • {t('pwaInstall.benefits.offline')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • {t('pwaInstall.benefits.fast')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • {t('pwaInstall.benefits.homeScreen')}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      {/* Top Banner for Install */}
      <Snackbar
        open={showBanner && !showPrompt}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 8, sm: 24 } }}
      >
        <Alert
          severity="info"
          icon={<HomeIcon />}
          action={
            <>
              <Button
                color="inherit"
                size="small"
                onClick={() => setShowPrompt(true)}
              >
                {t('pwaInstall.install')}
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleBannerClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
          sx={{ width: '100%' }}
        >
          {t('pwaInstall.bannerText')}
        </Alert>
      </Snackbar>

      {/* Install Dialog */}
      <Dialog
        open={showPrompt}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PhoneIcon color="primary" />
              <Typography variant="h6" component="span">
                {t('pwaInstall.title')}
              </Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('pwaInstall.description')}
          </Typography>

          {renderInstructions()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleClose} color="inherit">
            {t('pwaInstall.notNow')}
          </Button>
          {deferredPrompt && (
            <Button
              onClick={handleInstallClick}
              variant="contained"
              startIcon={<InstallIcon />}
            >
              {t('pwaInstall.install')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PWAInstallPrompt;
